import React, { useEffect, useState } from 'react';
import { Modal, Input, Button, App, List, Avatar, Divider, Form, Select, Typography, Space, DatePicker } from 'antd';
import { Localization, _T } from '../src/Localization';
import { apis, hooks, models } from '@vote4dance/judging-sdk';
var Api = apis.Api;
var Stream = hooks.streams.Stream;
var DanceTeamSource = models.danceteams.DanceTeamSource;
var DanceTeamUserItem = models.danceteams.DanceTeamUserItem;
var DanceTeamUsersSource = models.danceteams.DanceTeamUsersSource;
var OrganizationSource = models.organizations.OrganizationSource;
var UserSource = models.users.UserSource;
import { CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
var useSession = hooks.sessions.useSession;
var SessionState = hooks.sessions.SessionState;
import { useOrganization } from '../hooks/Organization';
import { ButtonConfirm } from './ButtonConfirm';
import dayjs from 'dayjs';
const { Text } = Typography;
export const DanceTeamEdit = (props) => {
    var _a, _b;
    const { item, organization_id, onOpen, onCreate, solo, buttonProps, autoIncludeCurrentUser = true } = props;
    const sessionState = useSession();
    const isSystemAdministrator = SessionState.isSystemAdministrator(sessionState);
    const { isCoach, isAdministrator } = useOrganization(organization_id);
    const [users] = Stream(item ? DanceTeamUsersSource(item.dance_team_id) : undefined);
    const [organizations, loadingOrganizations] = Stream(isAdministrator ? OrganizationSource : undefined);
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [isSearchingUser, setIsSearchingUser] = useState(false);
    const [createFallback, setCreateFallback] = useState(false);
    const [emailError, setEmailError] = useState(null);
    const { notification } = App.useApp();
    const [participants, setParticipants] = useState([]);
    const isSoloMode = typeof solo === 'boolean'
        ? solo
        : (item === null || item === void 0 ? void 0 : item.team_count) !== undefined
            ? parseInt(`${item.team_count}`) === 1
            : false;
    const [partnerMode, setPartnerMode] = useState(!isSoloMode);
    const [inputEmail, setInputEmail] = useState('');
    const [newUserInfo, setNewUserInfo] = useState({
        user_firstname: '',
        user_lastname: '',
        user_country: ((_a = sessionState === null || sessionState === void 0 ? void 0 : sessionState.userInfo) === null || _a === void 0 ? void 0 : _a.user_country) || undefined,
        user_language: ((_b = sessionState === null || sessionState === void 0 ? void 0 : sessionState.userInfo) === null || _b === void 0 ? void 0 : _b.user_language) || undefined,
        user_birthdate: undefined
    });
    const editDisabled = item ? (item.registration_count > 0 && !isSystemAdministrator) : false;
    const addParticipantWithDefaultRole = (user) => {
        setParticipants(prev => {
            if (prev.some(p => p.user_id === user.user_id))
                return prev;
            return [...prev, { ...user }];
        });
    };
    useEffect(() => {
        resetFields();
    }, [isCoach]);
    useEffect(() => {
        if (isSoloMode && partnerMode && participants.length <= 1) {
            setPartnerMode(false);
        }
        else if (!isSoloMode && !partnerMode) {
            setPartnerMode(true);
        }
    }, [isSoloMode]);
    useEffect(() => {
        if (form && item) {
            form.setFieldsValue(getDefaultValue());
            setParticipants(users || []);
        }
    }, [item, users]);
    const getDefaultValue = () => ({
        ...item
    });
    const openModal = () => {
        if (!item) {
            resetFields();
        }
        setVisible(true);
        if (form && item) {
            form.setFieldsValue(getDefaultValue());
            setParticipants(users || []);
        }
        if (onOpen) {
            onOpen();
        }
    };
    const handleCancel = () => {
        resetFields();
        setVisible(false);
    };
    const resetFields = () => {
        var _a, _b;
        setInputEmail('');
        setCreateFallback(false);
        setPartnerMode(!isSoloMode);
        setNewUserInfo({
            user_firstname: '',
            user_lastname: '',
            user_country: ((_a = sessionState === null || sessionState === void 0 ? void 0 : sessionState.userInfo) === null || _a === void 0 ? void 0 : _a.user_country) || undefined,
            user_language: ((_b = sessionState === null || sessionState === void 0 ? void 0 : sessionState.userInfo) === null || _b === void 0 ? void 0 : _b.user_language) || undefined,
            user_birthdate: undefined
        });
        if (!item && autoIncludeCurrentUser && !isCoach) {
            if (typeof autoIncludeCurrentUser === 'object') {
                setParticipants([{ ...autoIncludeCurrentUser }]);
            }
            else if (sessionState && sessionState.userInfo) {
                const me = sessionState.userInfo;
                setParticipants([{ ...me }]);
            }
            else {
                setParticipants([]);
            }
        }
        else {
            setParticipants(users || []);
        }
        if (form) {
            form.resetFields();
        }
    };
    const onFinish = (fieldsValue) => {
        setIsLoading(true);
        const updateItem = {
            ...item,
            ...fieldsValue,
            users: participants
        };
        setIsLoading(true);
        if (item) {
            Api.update(`${DanceTeamSource().url}/${item.dance_team_id}`, updateItem)
                .then(() => {
                resetFields();
                setIsLoading(false);
                setVisible(false);
            })
                .catch((err) => {
                setIsLoading(false);
                notification.error({
                    message: err.response && err.response.message ? _T(err.response.message) : err.message
                });
            });
        }
        else {
            Api.create(DanceTeamSource().url, updateItem)
                .then((result) => {
                resetFields();
                setIsLoading(false);
                setVisible(false);
                if (onCreate) {
                    onCreate(result);
                }
            })
                .catch((err) => {
                setIsLoading(false);
                notification.error({
                    message: err.response && err.response.message ? _T(err.response.message) : err.message
                });
            });
        }
    };
    const searchEmail = async (input) => {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(input)) {
            setEmailError(_T('login.emailInputMessage'));
            return;
        }
        setEmailError(null);
        setIsSearchingUser(true);
        await Api.call(DanceTeamSource().url, 'search-user', { term: input })
            .then((result) => {
            var _a, _b;
            const count = participants.length;
            addParticipantWithDefaultRole(result);
            setInputEmail('');
            setCreateFallback(false);
            setNewUserInfo({
                user_firstname: '',
                user_lastname: '',
                user_country: ((_a = sessionState === null || sessionState === void 0 ? void 0 : sessionState.userInfo) === null || _a === void 0 ? void 0 : _a.user_country) || undefined,
                user_language: ((_b = sessionState === null || sessionState === void 0 ? void 0 : sessionState.userInfo) === null || _b === void 0 ? void 0 : _b.user_language) || undefined,
                user_birthdate: undefined
            });
            // Clear pre-created team names
            if (item && item.team_name && item.team_updated_at === null && count === 0 && result) {
                form.setFieldValue('team_name', null);
            }
        })
            .catch((err) => {
            var _a;
            // If user not found, allow creation fallback
            const msg = ((_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.message) || (err === null || err === void 0 ? void 0 : err.message) || '';
            const notFound = typeof msg === 'string' && msg.toLowerCase().includes('not found');
            if (notFound) {
                setCreateFallback(true);
            }
            else {
                notification.error({
                    message: err.response ? err.response.message : err.message
                });
            }
        });
        setIsSearchingUser(false);
    };
    const remove = (item) => {
        setParticipants(prev => [...prev].filter(a => a.user_id !== item.user_id));
    };
    const onDeleteItem = async (item, done) => {
        await Api.destroy(`${DanceTeamSource().url}/${item.dance_team_id}`)
            .catch((err) => {
            notification.error({
                message: err.response && err.response.message ? _T(err.response.message) : err.message
            });
        });
        done();
    };
    const updateDanceRole = (user_id, role) => {
        setParticipants(prev => prev.map(p => p.user_id === user_id ? { ...p, dance_role: role } : p));
    };
    const showPartnerSearch = !isSoloMode || partnerMode;
    const hasEnoughParticipants = isSoloMode || participants.length > 1;
    const canCreateNewUser = Boolean(inputEmail &&
        newUserInfo.user_firstname &&
        newUserInfo.user_lastname &&
        newUserInfo.user_country &&
        newUserInfo.user_language &&
        newUserInfo.user_birthdate);
    const triggerDisabled = (editDisabled && !isSystemAdministrator) || (buttonProps && buttonProps.disabled);
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { icon: item ? React.createElement(EditOutlined, null) : React.createElement(PlusOutlined, null), onClick: openModal, disabled: triggerDisabled, ...buttonProps }, _T(item ? 'common.edit' : 'common.create', { name: _T('danceteam.' + (solo ? 'title' : 'plural')).toLowerCase() })),
        React.createElement(Modal, { title: _T(item ? 'common.edit' : 'common.add', { name: _T('danceteam.' + (solo ? 'title' : 'plural')).toLowerCase() }), open: visible, width: 700, onCancel: handleCancel, destroyOnHidden: true, footer: [
                item && item.dance_team_id &&
                    React.createElement(ButtonConfirm, { title: _T('common.delete.title'), description: _T('common.delete.content', { name: _T('danceteam.title').toLowerCase() }), onConfirm: (done) => onDeleteItem(item, done), key: "delete", disabled: item.registration_count > 0, buttonProps: {
                            danger: true,
                            icon: React.createElement(DeleteOutlined, null),
                            disabled: item.registration_count > 0
                        } }),
                React.createElement(Button, { key: "cancel", onClick: handleCancel }, _T('common.cancel.title')),
                React.createElement(Button, { key: "submit", type: "primary", form: `danceteam_settings_${item === null || item === void 0 ? void 0 : item.dance_team_id}`, htmlType: 'submit', loading: isLoading, disabled: !isSystemAdministrator && (editDisabled ||
                        participants.some(p => !p.dance_role) ||
                        !hasEnoughParticipants) }, _T('common.save'))
            ] },
            React.createElement(List, { dataSource: participants, footer: showPartnerSearch ? (React.createElement(React.Fragment, null,
                    React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
                        React.createElement(Text, { strong: true }, _T('danceteam.search')),
                        React.createElement(Input.Search, { placeholder: _T('login.email'), allowClear: true, type: 'email', enterButton: _T('common.search'), loading: isSearchingUser, onSearch: text => searchEmail(text), onPressEnter: e => {
                                e.preventDefault();
                                e.stopPropagation();
                                void searchEmail(inputEmail);
                            }, onChange: (e) => {
                                setInputEmail(e.target.value);
                                if (emailError)
                                    setEmailError(null);
                            }, value: inputEmail, disabled: editDisabled }),
                        emailError && React.createElement(Text, { type: "danger" }, emailError)),
                    createFallback && inputEmail && (React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
                        React.createElement(Divider, { style: { margin: '8px 0' } }, _T('registration.user.create')),
                        React.createElement(Input, { placeholder: _T('profile.details.user_firstname'), value: newUserInfo.user_firstname, onChange: e => setNewUserInfo(prev => ({ ...prev, user_firstname: e.target.value })), status: !newUserInfo.user_firstname ? 'error' : undefined, disabled: isLoading }),
                        React.createElement(Input, { placeholder: _T('profile.details.user_lastname'), value: newUserInfo.user_lastname, onChange: e => setNewUserInfo(prev => ({ ...prev, user_lastname: e.target.value })), status: !newUserInfo.user_lastname ? 'error' : undefined, disabled: isLoading }),
                        React.createElement(Select, { placeholder: _T('profile.details.user_country'), allowClear: true, showSearch: true, optionFilterProp: "label", value: newUserInfo.user_country, onChange: val => setNewUserInfo(prev => ({ ...prev, user_country: val })), options: Object.keys(Localization.getCountryNames()).map((code) => ({
                                value: code,
                                label: Localization.getCountryName(code),
                            })), status: !newUserInfo.user_country ? 'error' : undefined, disabled: isLoading }),
                        React.createElement(Select, { placeholder: _T('profile.details.user_language'), allowClear: true, value: newUserInfo.user_language, onChange: val => setNewUserInfo(prev => ({ ...prev, user_language: val })), options: [
                                { value: 'en', label: 'English' },
                                { value: 'sv', label: 'Svenska' },
                                { value: 'es', label: 'Español' },
                                { value: '', label: 'Auto' }
                            ], status: !newUserInfo.user_language ? 'error' : undefined, disabled: isLoading }),
                        React.createElement(DatePicker, { style: { width: '100%' }, placeholder: _T('profile.details.user_birthdate'), format: "YYYY-MM-DD", value: newUserInfo.user_birthdate ? dayjs(newUserInfo.user_birthdate) : undefined, onChange: (date) => {
                                setNewUserInfo(prev => ({
                                    ...prev,
                                    user_birthdate: date ? date.format('YYYY-MM-DD') : undefined
                                }));
                            }, status: !newUserInfo.user_birthdate ? 'error' : undefined, disabled: isLoading }),
                        React.createElement(Button, { type: "dashed", icon: React.createElement(PlusOutlined, null), loading: isLoading, disabled: !canCreateNewUser || isLoading, onClick: async () => {
                                var _a;
                                const { user_firstname, user_lastname, user_country, user_language, user_birthdate } = newUserInfo;
                                if (!user_firstname || !user_lastname || !user_country || !user_language || !user_birthdate) {
                                    notification.error({ message: _T('common.required') });
                                    return;
                                }
                                setIsLoading(true);
                                try {
                                    const origin = window.location.protocol + '//' + window.location.hostname + (window.location.port ? ':' + window.location.port : '');
                                    const created = await Api.create(UserSource().url, {
                                        user_email: inputEmail,
                                        user_firstname,
                                        user_lastname,
                                        user_country,
                                        user_language,
                                        user_birthdate,
                                        managed: true,
                                        origin
                                    });
                                    addParticipantWithDefaultRole(created);
                                    setInputEmail('');
                                    setCreateFallback(false);
                                    notification.success({ message: _T('registration.user.success') });
                                }
                                catch (err) {
                                    notification.error({
                                        message: ((_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.message) || (err === null || err === void 0 ? void 0 : err.message)
                                    });
                                }
                                finally {
                                    setIsLoading(false);
                                }
                            } }, _T('registration.user.create')))))) : (isSoloMode && (React.createElement(Button, { type: "dashed", icon: React.createElement(PlusOutlined, null), style: { marginTop: 12 }, onClick: () => setPartnerMode(true), disabled: editDisabled }, _T('danceteam.add_partner')))), renderItem: item => (React.createElement(List.Item, { actions: (editDisabled || (!isCoach && item.user_id === (sessionState === null || sessionState === void 0 ? void 0 : sessionState.sessionInfo.user_id)) ? [] : [
                        React.createElement(CloseOutlined, { onClick: () => remove(item) })
                    ]).concat(React.createElement(Select, { style: { minWidth: 120 }, options: [
                            { label: _T('danceteam.details.dance_role.leader'), value: 'leader' },
                            { label: _T('danceteam.details.dance_role.follower'), value: 'follower' },
                            { label: _T('danceteam.details.dance_role.none'), value: 'none' }
                        ], value: item.dance_role, status: item.dance_role === undefined ? 'error' : undefined, onChange: role => updateDanceRole(item.user_id, role), disabled: editDisabled, placeholder: _T('danceteam.details.dance_role.label') })) },
                    React.createElement(List.Item.Meta, { avatar: React.createElement(Avatar, { src: item.user_image }), title: DanceTeamUserItem.getFullName(item) }))) }),
            React.createElement(Form, { name: `danceteam_settings_${item === null || item === void 0 ? void 0 : item.dance_team_id}`, form: form, layout: "vertical", onFinish: onFinish, initialValues: getDefaultValue(), disabled: editDisabled },
                isSystemAdministrator && (React.createElement(Form.Item, { name: "team_name", label: _T('team.details.team_name.label') },
                    React.createElement(Input, { allowClear: true }))),
                React.createElement(Form.Item, { name: "organization_id", hidden: !isAdministrator },
                    React.createElement(Select, { style: { width: '100%' }, loading: loadingOrganizations, options: organizations === null || organizations === void 0 ? void 0 : organizations.map(a => ({
                            label: a.organization_name,
                            value: a.organization_id
                        })), placeholder: _T('organization.title'), disabled: isLoading || editDisabled, showSearch: true, allowClear: true, filterOption: (input, option) => {
                            if (!option) {
                                return false;
                            }
                            return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        } }))))));
};
